import React from 'react'
import {Col, Form, Row} from 'react-bootstrap'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import moment from 'moment'
import {TFormAllValues, TFormMemberPolicy} from '../../../../policies/policiesTypes'
import {FormikErrors, FormikTouched} from 'formik'
import {GENDER_OPTIONS, RELATIONSHIP_OPTIONS} from '../../../../../helpers/consts'
import {DateInput} from 'app/tfi/components/DateInput'
import {PhoneInput} from 'app/tfi/components/PhoneInput'
import {copyToClipboard} from '../../../../../../../utils/clipboardUtils'

const genderOptionMsg: Record<string, {id: string; defaultMessage: string}> = {
  Male: {id: 'common.gender.male', defaultMessage: 'Male'},
  Female: {id: 'common.gender.female', defaultMessage: 'Female'},
  Other: {id: 'common.option.other', defaultMessage: 'Other'},
}

const relationshipMsg = defineMessages({
  Self: {id: 'policies.detail.form.member.relationship.self', defaultMessage: 'Self'},
  Spouse: {id: 'policies.detail.form.member.relationship.spouse', defaultMessage: 'Spouse'},
  Child: {id: 'policies.detail.form.member.relationship.child', defaultMessage: 'Child'},
  'Mother or Father': {
    id: 'policies.detail.form.member.relationship.parent',
    defaultMessage: 'Mother or Father',
  },
  'Life Partner': {
    id: 'policies.detail.form.member.relationship.lifePartner',
    defaultMessage: 'Life Partner',
  },
  'Brother or Sister': {
    id: 'policies.detail.form.member.relationship.sibling',
    defaultMessage: 'Brother or Sister',
  },
  'Nephew or Niece': {
    id: 'policies.detail.form.member.relationship.niblings',
    defaultMessage: 'Nephew or Niece',
  },
  'Stepson or Stepdaughter': {
    id: 'policies.detail.form.member.relationship.stepchild',
    defaultMessage: 'Stepson or Stepdaughter',
  },
  Ward: {id: 'policies.detail.form.member.relationship.ward', defaultMessage: 'Ward'},
  'Grandson or Granddaughter': {
    id: 'policies.detail.form.member.relationship.grandchild',
    defaultMessage: 'Grandson or Granddaughter',
  },
  'Sponsored Dependent': {
    id: 'policies.detail.form.member.relationship.sponsoredDependent',
    defaultMessage: 'Sponsored Dependent',
  },
  'Collateral Dependent': {
    id: 'policies.detail.form.member.relationship.collateralDependent',
    defaultMessage: 'Collateral Dependent',
  },
  'Foster Child': {
    id: 'policies.detail.form.member.relationship.fosterChild',
    defaultMessage: 'Foster Child',
  },
  'Ex-Spouse': {
    id: 'policies.detail.form.member.relationship.exSpouse',
    defaultMessage: 'Ex-Spouse',
  },
  'Adopted Child': {
    id: 'policies.detail.form.member.relationship.adoptedChild',
    defaultMessage: 'Adopted Child',
  },
  'Court Appointed Guardian': {
    id: 'policies.detail.form.member.relationship.guardian',
    defaultMessage: 'Court Appointed Guardian',
  },
  'Dependent of a Minor Dependent': {
    id: 'policies.detail.form.member.relationship.dependentOfMinor',
    defaultMessage: 'Dependent of a Minor Dependent',
  },
  'Other Relative': {
    id: 'policies.detail.form.member.relationship.otherRelative',
    defaultMessage: 'Other Relative',
  },
  'Other Relationship': {
    id: 'policies.detail.form.member.relationship.otherRelationship',
    defaultMessage: 'Other Relationship',
  },
})

interface PersonalInformationProps {
  membernum: number
  valuesMember: TFormMemberPolicy
  errors: FormikErrors<TFormMemberPolicy>
  touched: FormikTouched<TFormMemberPolicy>
  allValues: TFormAllValues
  isHolder: boolean
  handleChange: (field: string, value: string | boolean) => void
  handleBlur: (field: string) => void
  disableEmail?: boolean
  disablePhone?: boolean
}

export const PersonalInformation: React.FC<PersonalInformationProps> = ({
  membernum,
  valuesMember,
  errors,
  touched,
  allValues,
  isHolder,
  handleChange,
  handleBlur,
  disableEmail = false,
  disablePhone = false,
}) => {
  const intl = useIntl()
  const selectOne = intl.formatMessage({
    id: 'common.placeholder.selectOption',
    defaultMessage: 'Select an option',
  })
  const copyTitle = (field: string) =>
    intl.formatMessage(
      {id: 'policies.detail.form.holder.copyField', defaultMessage: 'Copy {field}'},
      {field}
    )
  const dob = valuesMember.dateBirth
    ? intl.formatMessage(
        {id: 'policies.detail.form.member.ageValue', defaultMessage: 'Age: {age}'},
        {age: moment().diff(moment(valuesMember.dateBirth), 'years')}
      )
    : intl.formatMessage({id: 'policies.detail.form.member.ageNone', defaultMessage: 'Age: -'})
  return (
    <div>
      <Row>
        <Col md={6}>
          <Form.Group>
            <Form.Label>
              <FormattedMessage
                id='policies.list.wizard.holder.isApplicant'
                defaultMessage='Is this member an applicant?'
              />
            </Form.Label>
            <Form.Select
              name='applicant'
              value={valuesMember.applicant ? 'yes' : 'no'}
              onChange={(e) => {
                handleChange('applicant', e.target.value === 'yes')
              }}
            >
              <option value='yes'>{intl.formatMessage({id: 'common.boolean.yes', defaultMessage: 'Yes'})}</option>
              <option value='no'>{intl.formatMessage({id: 'common.boolean.no', defaultMessage: 'No'})}</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>
              <FormattedMessage
                id='policies.list.wizard.holder.preferredLanguage'
                defaultMessage='Preferred Language'
              />
            </Form.Label>
            <Form.Select
              value={valuesMember.preferredLenguage ?? ''}
              name='preferredLenguage'
              onChange={(e) => {
                handleChange('preferredLenguage', e.target.value)
              }}
            >
              <option value='english'>{intl.formatMessage({id: 'common.language.english', defaultMessage: 'English'})}</option>
              <option value='spanish'>{intl.formatMessage({id: 'common.language.spanish', defaultMessage: 'Spanish'})}</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Form.Group>
            <Form.Label className='d-flex align-items-center gap-2'>
              <i
                className='bi bi-files'
                onClick={() => copyToClipboard(valuesMember.firstName, 'First Name')}
                title={copyTitle(intl.formatMessage({id: 'common.field.firstName', defaultMessage: 'First Name'}))}
                style={{fontSize: '0.85rem', cursor: 'pointer', color: '#009ef7'}}
              ></i>
              <span>
                <FormattedMessage id='common.field.firstName' defaultMessage='First Name' /> <small className='text-danger'>{errors && errors.firstName}</small>
              </span>
            </Form.Label>
            <Form.Control
              name='firstName'
              placeholder={intl.formatMessage({id: 'common.placeholder.enterFirstName', defaultMessage: 'Enter first name'})}
              id='firstName'
              required
              value={valuesMember.firstName ?? ''}
              onBlur={() => handleBlur('firstName')}
              onChange={(e) => {
                handleChange('firstName', e.target.value)
              }}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label htmlFor='middleName' className='d-flex align-items-center gap-2'>
              <i
                className='bi bi-files'
                onClick={() => copyToClipboard(valuesMember.middleName, 'Middle Name')}
                title={copyTitle(intl.formatMessage({id: 'common.field.middleName', defaultMessage: 'Middle Name'}))}
                style={{fontSize: '0.85rem', cursor: 'pointer', color: '#009ef7'}}
              ></i>
              <span>
                <FormattedMessage id='common.field.middleName' defaultMessage='Middle Name' />
              </span>
            </Form.Label>
            <Form.Control
              name='middleName'
              id='middleName'
              value={valuesMember.middleName ?? ''}
              onChange={(e) => {
                handleChange('middleName', e.target.value)
              }}
              placeholder={intl.formatMessage({id: 'common.placeholder.optional', defaultMessage: '(optional)'})}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Form.Group>
            <Form.Label htmlFor='lastName' className='d-flex align-items-center gap-2'>
              <i
                className='bi bi-files'
                onClick={() => copyToClipboard(valuesMember.lastName, 'Last Name')}
                title={copyTitle(intl.formatMessage({id: 'common.field.lastName', defaultMessage: 'Last Name'}))}
                style={{fontSize: '0.85rem', cursor: 'pointer', color: '#009ef7'}}
              ></i>
              <span>
                <FormattedMessage id='common.field.lastName' defaultMessage='Last Name' /> <small className='text-danger'>{errors.lastName}</small>
              </span>
            </Form.Label>
            <Form.Control
              name='lastName'
              placeholder={intl.formatMessage({id: 'common.placeholder.enterLastName', defaultMessage: 'Enter last name'})}
              value={valuesMember.lastName ?? ''}
              onChange={(e) => {
                handleChange('lastName', e.target.value)
              }}
              onBlur={() => handleBlur('lastName')}
              id='lastName'
              className='form-control '
              required
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label htmlFor='secondLastName' className='d-flex align-items-center gap-2'>
              <i
                className='bi bi-files'
                onClick={() => copyToClipboard(valuesMember.secondLastName, 'Second Last Name')}
                title={copyTitle(intl.formatMessage({id: 'common.field.secondLastName', defaultMessage: 'Second Last Name'}))}
                style={{fontSize: '0.85rem', cursor: 'pointer', color: '#009ef7'}}
              ></i>
              <span>
                <FormattedMessage id='common.field.secondLastName' defaultMessage='Second Last Name' />
              </span>
            </Form.Label>
            <Form.Control
              name='secondLastName'
              value={valuesMember.secondLastName ?? ''}
              onChange={(e) => {
                handleChange('secondLastName', e.target.value)
              }}
              id='secondLastName'
              placeholder={intl.formatMessage({id: 'common.placeholder.optional', defaultMessage: '(optional)'})}
            />
          </Form.Group>
        </Col>
      </Row>
      {!isHolder && (
        <Row className='member-rol'>
          <Col md={6}>
            <Form.Group>
              <Form.Label htmlFor='rol'>
                <FormattedMessage
                  id='policies.list.preview.table.role'
                  defaultMessage='Role'
                />
              </Form.Label>
              <Form.Select
                value={valuesMember.rol ?? ''}
                name='rol'
                id='rol'
                onChange={(e) => {
                  handleChange('rol', e.target.value)
                }}
              >
                {/* <option value=''>Select one</option> */}
                {/* <option value='HOLDER'>Holder</option> */}
                <option value='FAMILY'>
                  {intl.formatMessage({id: 'policies.detail.form.member.rolFamily', defaultMessage: 'Family'})}
                </option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label htmlFor='rol'>
                <FormattedMessage
                  id='policies.list.wizard.family.relationship'
                  defaultMessage='Relationship'
                />
              </Form.Label>
              <Form.Select
                value={valuesMember.relationship ?? ''}
                name='relationship'
                id='relationship'
                onChange={(e) => {
                  handleChange('relationship', e.target.value)
                }}
              >
                {/* <option value=''>Select one</option> */}
                {/* <option value='HOLDER'>Holder</option> */}
                {RELATIONSHIP_OPTIONS.map(({value, label}) => (
                  <option key={value} value={value}>
                    {relationshipMsg[value as keyof typeof relationshipMsg]
                      ? intl.formatMessage(relationshipMsg[value as keyof typeof relationshipMsg])
                      : label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
      )}
      <Row>
        <Col md={6}>
          <Form.Group>
            <Form.Label className='label-date-of-birth-age d-flex justify-content-between align-items-center'>
              <span className='d-flex align-items-center gap-2'>
                <i
                  className='bi bi-files'
                  onClick={() => {
                    const dobStr = valuesMember.dateBirth ? moment(valuesMember.dateBirth).format('MM/DD/YYYY') : ''
                    copyToClipboard(dobStr, 'Date of Birth')
                  }}
                  title={copyTitle(intl.formatMessage({id: 'common.field.dateOfBirth', defaultMessage: 'Date of Birth'}))}
                  style={{fontSize: '0.85rem', cursor: 'pointer', color: '#009ef7'}}
                ></i>
                <span>
                  <FormattedMessage id='common.field.dateOfBirth' defaultMessage='Date of Birth' /> <small className='text-danger'>{errors.dateBirth}</small>
                </span>
              </span>
              <span className='age'>{dob}</span>
            </Form.Label>
            <DateInput
              id="dateBirth"
              name="dateBirth"
              label=""
              value={valuesMember.dateBirth}
              error={errors.dateBirth}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label htmlFor='gender'>
              <FormattedMessage id='common.field.gender' defaultMessage='Gender' /> <small className='text-danger'>{errors.gender}</small>
            </Form.Label>
            <Form.Select
              name='gender'
              id='gender'
              value={valuesMember.gender ?? ''}
              onBlur={() => handleBlur('gender')}
              onChange={(e) => {
                handleChange('gender', e.target.value)
              }}
            >
              <option value=''>{selectOne}</option>
              {GENDER_OPTIONS.map(({value, label}) => (
                <option key={value} value={value}>
                  {genderOptionMsg[value] ? intl.formatMessage(genderOptionMsg[value]) : label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Form.Group>
            <Form.Label htmlFor='emailAddress' className='d-flex align-items-center gap-2'>
              <i
                className='bi bi-files'
                onClick={() => copyToClipboard(valuesMember.emailAddress, 'Email')}
                title={copyTitle(intl.formatMessage({id: 'common.field.email', defaultMessage: 'Email'}))}
                style={{fontSize: '0.85rem', cursor: 'pointer', color: '#009ef7'}}
              ></i>
              <span>
                <FormattedMessage id='common.field.email' defaultMessage='Email' /> <small className='text-danger'>{errors.emailAddress}</small>
              </span>
            </Form.Label>
            <Form.Control
              type='email'
              name='emailAddress'
              value={valuesMember.emailAddress ?? ''}
              onBlur={() => handleBlur('emailAddress')}
              onChange={(e) => {
                handleChange('emailAddress', e.target.value)
              }}
              id='emailAddress'
              autoComplete='off'
              disabled={disableEmail}
              readOnly={disableEmail}
            />
            {disableEmail && (
              <small className='text-muted'>
                <FormattedMessage
                  id='policies.detail.form.member.emailLockedNote'
                  defaultMessage='Advisors cannot edit the email of a completed policy.'
                />
              </small>
            )}
          </Form.Group>
        </Col>
        <Col md={6}>
          <PhoneInput
            id="phone"
            name="phone"
            label={
              <span className='d-flex align-items-center gap-2'>
                <i
                  className='bi bi-files'
                  onClick={() => copyToClipboard(valuesMember.phone, 'Phone')}
                  title={copyTitle(intl.formatMessage({id: 'common.field.phone', defaultMessage: 'Phone'}))}
                  style={{fontSize: '0.85rem', cursor: 'pointer', color: '#009ef7'}}
                ></i>
                <span>
                  <FormattedMessage id='common.field.phone' defaultMessage='Phone' /> <small className='text-danger'>{errors.phone}</small>
                </span>
              </span>
            }
            value={valuesMember.phone}
            error={errors.phone}
            touched={touched.phone}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={disablePhone}
            readOnly={disablePhone}
          />
          {disablePhone && (
            <small className='text-muted'>
              <FormattedMessage
                id='policies.detail.form.member.phoneLockedHint'
                defaultMessage='Advisors cannot edit the phone of a completed policy.'
              />
            </small>
          )}
        </Col>
      </Row>
      <Row>
        <Col md={3}>
          <Form.Check
            label={intl.formatMessage({id: 'policies.detail.form.member.tobacco', defaultMessage: 'Tobacco user'})}
            type='switch'
            name={
              membernum === 0 ? 'holder.usesTobacco' : `policyMembers[${membernum - 1}].usesTobacco`
            }
            id={`usesTobacco_${membernum}`}
            checked={
              typeof valuesMember.usesTobacco === 'string'
                ? valuesMember.usesTobacco === 'true'
                : valuesMember.usesTobacco
            }
            onChange={(e) => {
              console.log(String(e.target.checked))
              handleChange('usesTobacco', String(e.target.checked))
            }}
          />
        </Col>
        <Col md={3}>
          <Form.Check
            label={intl.formatMessage({id: 'policies.detail.form.member.pregnant', defaultMessage: 'Pregnant'})}
            type='switch'
            name={
              membernum === 0 ? 'holder.isPregnant' : `policyMembers[${membernum - 1}].isPregnant`
            }
            id={`isPregnant_${membernum}`}
            checked={
              typeof valuesMember.isPregnant === 'string'
                ? valuesMember.isPregnant === 'true'
                : valuesMember.isPregnant
            }
            onChange={(e) => {
              handleChange('isPregnant', String(e.target.checked))
            }}
          />
        </Col>
        <Col md={6}>
          <Form.Check
            label={intl.formatMessage({
              id: 'policies.detail.form.member.eligibleCoverage',
              defaultMessage: 'Eligible for health coverage through a job, Medicare, Medicaid, or CHIP',
            })}
            type='switch'
            name={
              membernum === 0 ? 'holder.hasMec' : `policyMembers[${membernum - 1}].hasMec`
            }
            id={`hasMec_${membernum}`}
            checked={
              typeof valuesMember.hasMec === 'string'
                ? valuesMember.hasMec === 'true'
                : valuesMember.hasMec
            }
            onChange={(e) => {
              handleChange('hasMec', String(e.target.checked))
            }}
          />
        </Col>
      </Row>
    </div>
  )
}
